import {
  CellClickedEvent,
  ColDef,
  GetQuickFilterTextParams,
  GetRowIdParams,
  GridReadyEvent,
  ICellRendererParams,
  ITooltipParams,
  RowClassParams,
  RowClassRules,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import {
  memo,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import isEqual from "lodash/isEqual";

import "@ag-grid-community/styles/ag-grid.css";
import "@ag-grid-community/styles/ag-theme-alpine.css";
// Local
import "../../assets/css/ag-grid-override-icon.css";
// This is for tables that use percentage-based heights - ie `h-full min-h-[500px]`
import "../../assets/css/ag-grid-inject-height.css";

import {
  ExistingStiltInvoice,
  getReadableStiltInvoiceStatus,
} from "../../models/invoice";
import currencyFormatter from "../../currency";
import InvoiceStatusCustomFilter from "../tables/InvoiceStatusCustomFilter";
import SiteKeyLocationCustomFilter from "../tables/SiteKeyLocationCustomFilter";
import { ExistingSiteKeyLocation } from "../../models/site-key-location";
import SearchBox from "../SearchBox";
import { getTimestampFormatted } from "../../assets/js/agGridUtils";
import BaseButtonPrimary from "../BaseButtonPrimary";
import { QBOInvoiceResponse } from "../../Pages/Quickbooks/QuickbooksSyncErrorsContainer";

interface Props {
  /** invoices that have an accountingRefID */
  invoiceList: ExistingStiltInvoice[];
  onViewInvoice: (invoiceID: string, acctRefID: string | null) => void;
  currency: string;
  siteKeyLocationList: ExistingSiteKeyLocation[];
  onImportSelected: (invoiceIDs: string[]) => Promise<void>;
  qboInvoices: QBOInvoiceResponse[] | undefined;
  qboInvoicesLoading: boolean;
  /** default is max-h-[47vh] */
  tableMaxHeight?: string;

  children: {
    dateRangePicker: React.ReactNode;
    qboLoadingInvoicesIndicator: React.ReactNode;
  };
}

/** invoice table with built-in search box and import button, for QBO imports */
export default function InvoiceTable(props: Props): JSX.Element {
  const [isBusy, setIsBusy] = useState(false);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [gridReady, setGridReady] = useState(false);
  const gridRef = useRef<AgGridReact>(null);

  const onGridReady = useCallback(() => {
    setGridReady(true);
  }, []);

  useLayoutEffect(() => {
    if (gridRef.current && gridReady) gridRef.current.api?.sizeColumnsToFit();
  }, [gridReady]);

  const tableData: StiltQboInvoiceData[] = useMemo(() => {
    const list: StiltQboInvoiceData[] = [];
    for (const invoice of props.invoiceList) {
      const qboInvID = invoice.customData.accountingRefID;

      if (props.qboInvoicesLoading || !props.qboInvoices) {
        list.push({ ...invoice, qboTotal: null, qboBalance: null });
        continue;
      }

      const found = props.qboInvoices.find((i) => i.id === qboInvID);
      if (!found) {
        list.push({ ...invoice, qboTotal: null, qboBalance: null });
        continue;
      }

      list.push({
        ...invoice,
        qboTotal: found.total,
        qboBalance: found.balance,
      });
    }
    return list;
  }, [props.invoiceList, props.qboInvoices, props.qboInvoicesLoading]);

  function onSearch(event: React.ChangeEvent<HTMLInputElement>) {
    const searchValue = event.target.value;
    gridRef.current?.api.setGridOption("quickFilterText", searchValue);
  }

  const selectedRowsIcon = (
    <span
      className={`inline-block rounded-full font-medium leading-none
    ${selectedRows.length > 9 ? "p-1.5" : "px-2 py-1"}
    ${selectedRows.length ? "bg-primary text-primaryButtonText" : "bg-gray-100 text-gray-300"}`}
    >
      {selectedRows.length}
    </span>
  );

  const importSelectedButton = (
    <BaseButtonPrimary
      onClick={async () => {
        setIsBusy(true);
        try {
          await props.onImportSelected(selectedRows.map((row) => row.id));
          // clear row selection
          gridRef.current?.api.deselectAll();
          setSelectedRows([]);
        } finally {
          setIsBusy(false);
        }
      }}
      isBusy={isBusy}
      busyText="Importing..."
      disabled={selectedRows.length === 0 || isBusy}
    >
      Import Selected
    </BaseButtonPrimary>
  );

  return (
    <>
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-end sm:justify-between">
        <SearchBox
          onInput={onSearch}
          id="search-box"
          widthClasses="w-full sm:max-w-sm"
          overrideShrink=""
        />

        {/* layout depends on whether date range picker is provided */}
        {props.children.dateRangePicker ? (
          <div className="flex shrink-0 flex-col items-end gap-2 self-end">
            <span className="flex items-center gap-2">
              {selectedRowsIcon}
              {importSelectedButton}
            </span>

            <span className="flex items-center gap-3 sm:ml-4">
              {props.children.qboLoadingInvoicesIndicator}
              {props.children.dateRangePicker}
            </span>
          </div>
        ) : (
          <div className="flex shrink-0 items-center gap-4 self-end">
            {props.children.qboLoadingInvoicesIndicator}
            <span className="flex items-center gap-2">
              {selectedRowsIcon}
              {importSelectedButton}
            </span>
          </div>
        )}
      </div>

      <div
        className={`ag-theme-alpine mb-4 flex h-[1000px] flex-col ${props.tableMaxHeight ?? "max-h-[47vh]"}`}
      >
        {/* ^ mb-4 offsets the table's mt-4 */}
        <TheTable
          tableData={tableData}
          onViewInvoice={props.onViewInvoice}
          currency={props.currency}
          rowSelectionChanged={(ev) => {
            setSelectedRows(ev.api.getSelectedRows());
          }}
          gridRef={gridRef}
          onGridReady={onGridReady}
          siteKeyLocationList={props.siteKeyLocationList}
        />
      </div>
    </>
  );
}

type StiltQboInvoiceData = ExistingStiltInvoice & {
  qboTotal: number | null;
  qboBalance: number | null;
};

interface TableProps {
  tableData: StiltQboInvoiceData[];
  siteKeyLocationList: ExistingSiteKeyLocation[];
  currency: string;
  onViewInvoice: (invoiceID: string, acctRefID: string | null) => void;

  rowSelectionChanged: (event: GridReadyEvent) => void;
  gridRef: React.MutableRefObject<AgGridReact<any> | null>;
  onGridReady: (event: GridReadyEvent) => void;
}

const commonColProps = {
  width: 150,
  flex: 1,
  resizable: true,
  // filter: true,
  sortable: true,
  headerClass:
    "pl-6 pr-3 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase",
  tooltipValueGetter: (params: ITooltipParams) =>
    params.valueFormatted ?? params.value,
};

const TheTable = memo(
  ({
    tableData,
    onViewInvoice,
    currency,
    rowSelectionChanged,
    gridRef,
    onGridReady,
    siteKeyLocationList,
  }: TableProps) => {
    const getLocationTitle = (locationID: string): string => {
      const found = siteKeyLocationList.find((loc) => loc.id === locationID);
      return found ? found.title : "[unknown]";
    };

    const renderStatus = (params: ICellRendererParams): JSX.Element => {
      let customBg = "bg-primary text-primaryButtonText";
      switch (params.data.status) {
        case "paid":
          customBg = "bg-greenPass text-white";
          break;
        case "partiallyPaid":
          customBg = "bg-orangeError text-white";
          break;
        case "canceled":
          customBg = "bg-gray-200 text-black";
          break;
        case "draft":
          customBg = "bg-gray-200 text-black";
          break;
      }
      return (
        <div
          className={`rounded-full px-2.5 py-1 text-xs font-medium ${customBg} capitalize`}
        >
          {getReadableStiltInvoiceStatus(params.data.status)}
        </div>
      );
    };

    const renderStiltAmounts = (params: ICellRendererParams): JSX.Element => {
      const amountDue = params.data.amountDue;
      const totalAmount = params.data.totalAmount;
      return (
        <div
          className={params.data.status === "canceled" ? "line-through" : ""}
        >
          {currencyFormatter(amountDue, currency)} /{" "}
          {currencyFormatter(totalAmount, currency)}
        </div>
      );
    };

    const renderQBOAmounts = (params: ICellRendererParams): JSX.Element => {
      const acctRefID = params.data.customData.accountingRefID;
      if (!acctRefID) return <div>--</div>;

      const qboTotal = params.data.qboTotal;
      const qboBalance = params.data.qboBalance;
      if (qboTotal === null && qboBalance === null) return <div>--</div>;

      let total;
      if (typeof qboTotal !== "number") total = "--";
      else total = currencyFormatter(qboTotal, currency);

      let balance;
      if (typeof qboBalance !== "number") balance = "--";
      else balance = currencyFormatter(qboBalance, currency);

      return (
        <div>
          {balance} / {total}
        </div>
      );
    };

    /** QFT = quick filter text lol sry */
    const getQboAmountsQFT = (params: GetQuickFilterTextParams): string => {
      if (!params.data.customData.accountingRefID) return "";

      const balance = params.data.qboBalance;
      let uiBalance;
      if (typeof balance !== "number") uiBalance = "";
      else uiBalance = `${balance} ${currencyFormatter(balance, currency)}`;

      const total = params.data.qboTotal;
      let uiTotal;
      if (typeof total !== "number") uiTotal = "";
      else uiTotal = `${total} ${currencyFormatter(total, currency)}`;

      return `${uiBalance} ${uiTotal}`;
    };

    /** TVG = tooltip value getter */
    const getQboAmountsTVG = (params: ITooltipParams): string => {
      if (!params.data.customData.accountingRefID) return "";

      const balance = params.data.qboBalance;
      let uiBalance;
      if (typeof balance !== "number") uiBalance = "--";
      else uiBalance = currencyFormatter(balance, currency);

      const total = params.data.qboTotal;
      let uiTotal;
      if (typeof total !== "number") uiTotal = "--";
      else uiTotal = currencyFormatter(total, currency);

      return `Amount Due: ${uiBalance} / Total Amount: ${uiTotal}`;
    };

    // highlight rows where Stilt totals don't match QBO totals
    const rowClassRules: RowClassRules = useMemo(() => {
      return {
        "ag-stilt-highlight": (params: RowClassParams) => {
          if (!params.data.customData.accountingRefID) return false;

          const qboTotal = params.data.qboTotal;
          const qboBalance = params.data.qboBalance;
          if (qboTotal === null && qboBalance === null) return false;

          if (qboTotal === 0 && params.data.status === "canceled") return false;

          if (qboTotal !== params.data.totalAmount) return true;
          if (qboBalance !== params.data.amountDue) return true;
          return false;
        },
      };
    }, []);

    function handleCellClick(params: CellClickedEvent) {
      onViewInvoice(
        params.data.id,
        params.data.customData.accountingRefID ?? null,
      );
    }

    const columnDefs: ColDef[] = [
      {
        // TODO: find other places we're using .sort on whatever becomes rowData, replace
        // FOR SORTING BY issueDate VALUES
        field: "issueDate",
        hide: true,
        sort: "desc",
      },
      {
        headerName: "Inv. #",
        field: "invoiceNumber",
        checkboxSelection: true,
        headerCheckboxSelection: true,
        headerCheckboxSelectionFilteredOnly: true,
      },
      {
        headerName: "Customer Name",
        field: "name",
        flex: 1.5,
        onCellClicked: handleCellClick,
      },
      {
        headerName: "Stilt Amounts",
        cellRenderer: renderStiltAmounts,
        onCellClicked: handleCellClick,
        tooltipValueGetter: (params: ITooltipParams) =>
          `Amount Due: ${currencyFormatter(params.data.amountDue, currency)} / Total Amount: ${currencyFormatter(params.data.totalAmount, currency)}`,
        getQuickFilterText: (params: GetQuickFilterTextParams) =>
          `${params.data.amountDue} ${params.data.totalAmount} ${currencyFormatter(params.data.amountDue, currency)} ${currencyFormatter(params.data.totalAmount, currency)}`,
      },
      {
        headerName: "QB Amounts",
        cellRenderer: renderQBOAmounts,
        onCellClicked: handleCellClick,
        tooltipValueGetter: getQboAmountsTVG,
        getQuickFilterText: getQboAmountsQFT,
      },
      {
        field: "status",
        filter: InvoiceStatusCustomFilter,
        cellRenderer: renderStatus,
        cellStyle: {
          height: "100%",
          display: "flex",
          justifyContent: "start",
        },
        valueFormatter: (params) => getReadableStiltInvoiceStatus(params.value),
        onCellClicked: handleCellClick,
      },
      {
        headerName: "Issue Date",
        field: "issueDate",
        valueFormatter: getTimestampFormatted,
        onCellClicked: handleCellClick,
      },
    ];

    if (siteKeyLocationList.length > 1) {
      //   columnDefs.splice(columnDefs.length - 1, 0, {
      columnDefs.push({
        field: "locationID",
        headerName: "Site Location",
        cellRenderer: (params: ICellRendererParams) =>
          getLocationTitle(params.data.locationID),
        getQuickFilterText: (params: GetQuickFilterTextParams) =>
          getLocationTitle(params.data.locationID),
        valueFormatter: (params) => getLocationTitle(params.data.locationID),
        filter: SiteKeyLocationCustomFilter,
        onCellClicked: handleCellClick,
      });
    }

    // providing row IDs is "strongly recommended" https://www.ag-grid.com/react-data-grid/row-ids/
    const getRowID = useCallback(
      (params: GetRowIdParams) => params.data.id,
      [],
    );

    return (
      <AgGridReact
        reactiveCustomComponents
        ref={gridRef}
        onGridReady={onGridReady}
        getRowId={getRowID}
        defaultColDef={commonColProps}
        columnDefs={columnDefs}
        className="mt-4 shadow"
        rowData={tableData}
        rowSelection="multiple"
        suppressRowClickSelection={true}
        cacheQuickFilter={true}
        onSelectionChanged={(ev) => rowSelectionChanged(ev)}
        suppressMenuHide={true}
        // icons={icons}
        rowClassRules={rowClassRules}
      />
    );
  },
  (prev, next) => isEqual(prev.tableData, next.tableData),
);
